import type {
  BooleanCustomFieldDefinition,
  DateCustomFieldDefinition,
  EmailCustomFieldDefinition,
  FieldsetCustomFieldDefinition,
  MultiSelectCustomFieldDefinition,
  NumberCustomFieldDefinition,
  SelectCustomFieldDefinition,
  TextareaCustomFieldDefinition,
  TextCustomFieldDefinition,
  UploadCustomFieldDefinition,
  UrlCustomFieldDefinition,
  UserCustomFieldDefinition,
} from '../index';

export const CUSTOM_FIELD_TYPE_VALUES = [
  'textarea',
  'upload',
  'text',
  'email',
  'url',
  'select',
  'multi-select',
  'boolean',
  'number',
  'date',
  'fieldset',
  'user',
] as const;

export type CustomFieldType = (typeof CUSTOM_FIELD_TYPE_VALUES)[number];

type LeafFieldValue =
  | string
  | string[]
  | number
  | boolean
  | { title?: string; url: string }
  | { url: string; mimeType: string; thumbnailUrl?: string; name: string }[]
  | { userId: string }
  | null;

// Recursive interface for fieldset values - runtime validation enforces MAX_FIELDSET_DEPTH
export interface FieldsetValue {
  [key: string]: FieldsetValue | LeafFieldValue | null;
}

export type CustomFieldHelperType = {
  ['textarea']: {
    value: string;
    definition: TextareaCustomFieldDefinition;
  };
  ['text']: {
    value: string;
    definition: TextCustomFieldDefinition;
  };
  ['number']: {
    value: number;
    definition: NumberCustomFieldDefinition;
  };
  ['date']: {
    /** ISO-8601 full-date, e.g. `2024-01-15` */
    value: string;
    definition: DateCustomFieldDefinition;
  };
  ['email']: {
    value: string;
    definition: EmailCustomFieldDefinition;
  };
  ['upload']: {
    value: {
      url: string;
      mimeType: string;
      thumbnailUrl?: string;
      name: string;
    }[];
    definition: UploadCustomFieldDefinition;
  };
  ['select']: {
    value: string;
    definition: SelectCustomFieldDefinition;
  };
  ['multi-select']: {
    value: string[];
    definition: MultiSelectCustomFieldDefinition;
  };
  ['boolean']: {
    value: boolean;
    definition: BooleanCustomFieldDefinition;
  };
  ['url']: {
    value: { title?: string; url: string };
    definition: UrlCustomFieldDefinition;
  };
  ['fieldset']: {
    value: FieldsetValue;
    definition: FieldsetCustomFieldDefinition;
  };
  ['user']: {
    value: {
      userId: string;
    };
    definition: UserCustomFieldDefinition;
  };
};

export type CustomFieldFormatters = {
  [K in CustomFieldType]?: (value: CustomFieldHelperType[K]['value']) => string;
};

export type CustomFieldValue<T extends CustomFieldType> = {
  identifier: string;
  value: CustomFieldHelperType[T]['value'] | null;
};

export type CustomFieldWithValue<T extends CustomFieldType> = {
  field: CustomFieldHelperType[T]['definition'];
  value?: CustomFieldValue<T>['value'];
};

export const SCHEMA_TYPE_VALUES = [
  'string',
  'number',
  'boolean',
  'object',
  'null',
  'array',
] as const;

type SchemaType = (typeof SCHEMA_TYPE_VALUES)[number];

export type Schema = Record<string, unknown> & {
  metadata: {
    'payaca:label': string;
    'payaca:field-type': CustomFieldType;
    'payaca:identifier': string;
    'payaca:sort-index'?: number;
    'payaca:description'?: string;
    /**
     * Shared across the types that offer a display mode. The valid subset
     * depends on the field type — each definition's `displayMode` accessor
     * narrows it (`select` to dropdown/radio, `multi-select` to
     * dropdown/checkbox).
     */
    'payaca:display-mode'?: 'dropdown' | 'radio' | 'checkbox';
  } & Record<string, unknown>;
  type: SchemaType[] | SchemaType;
  enum?: (string | null)[];
  properties?: Record<string, Partial<Schema>>;
};

// =============================================================================
// Internal base types
// =============================================================================

/** Common properties shared by all custom field types (excluding identifier) */
type BaseCustomFieldProperties = {
  label: string;
  isRequired?: boolean;
  description?: string;
};

/** Properties specific to select fields */
type SelectFieldProperties = {
  options: string[];
  displayMode?: 'dropdown' | 'radio';
  type: 'select';
};

/** How a multi-select field presents its options when being filled in */
export const MULTI_SELECT_DISPLAY_MODE_VALUES = [
  'dropdown',
  'checkbox',
] as const;
export type MultiSelectDisplayMode =
  (typeof MULTI_SELECT_DISPLAY_MODE_VALUES)[number];

/** Properties specific to multi-select fields */
type MultiSelectFieldProperties = {
  options: string[];
  displayMode?: MultiSelectDisplayMode;
  /** Most options that may be chosen. Defaults to the number of options. */
  limit?: number;
  type: 'multi-select';
};

/** Canonical set of upload media types an upload field can accept */
export const UPLOAD_TYPE_VALUES = ['image', 'document', 'video'] as const;
export type UploadType = (typeof UPLOAD_TYPE_VALUES)[number];

/** Properties specific to upload fields */
type UploadFieldProperties = {
  limit?: number;
  uploadType?: UploadType[];
  type: 'upload';
};

/** Properties specific to simple fields (non-compound types) */
type SimpleFieldProperties = {
  type:
    | 'number'
    | 'boolean'
    | 'textarea'
    | 'text'
    | 'email'
    | 'url'
    | 'user'
    | 'date';
};

// =============================================================================
// User Input Types (identifier optional)
// Use these for GraphQL/REST input before internal processing
// =============================================================================

/**
 * Base type for user-facing custom field input.
 * identifier is optional and will be generated from label if not provided.
 */
export type BasicCustomFieldInput = BaseCustomFieldProperties & {
  identifier?: string | null;
};

export type SelectCustomFieldInput = BasicCustomFieldInput &
  SelectFieldProperties;

export type MultiSelectCustomFieldInput = BasicCustomFieldInput &
  MultiSelectFieldProperties;

export type UploadCustomFieldInput = BasicCustomFieldInput &
  UploadFieldProperties;

export type SimpleCustomFieldInput = BasicCustomFieldInput &
  SimpleFieldProperties;

/**
 * FieldsetCustomFieldInput is recursive - runtime validation enforces
 * the MAX_FIELDSET_DEPTH limit to prevent arbitrarily deep nesting.
 */
export type FieldsetCustomFieldInput = BasicCustomFieldInput & {
  children: (
    | SelectCustomFieldInput
    | MultiSelectCustomFieldInput
    | SimpleCustomFieldInput
    | UploadCustomFieldInput
    | FieldsetCustomFieldInput
  )[];
  type: 'fieldset';
};

/**
 * Union of all custom field input types for user-facing APIs.
 * identifier is optional on all variants.
 */
export type CustomFieldInput =
  | FieldsetCustomFieldInput
  | SimpleCustomFieldInput
  | SelectCustomFieldInput
  | MultiSelectCustomFieldInput
  | UploadCustomFieldInput;

// =============================================================================
// Definition Types (identifier required)
// Use these after internal processing ensuring identifier is set
// =============================================================================

/**
 * Base type for processed custom field definitions with guaranteed identifier.
 * Use `BasicCustomFieldInput` for user-facing input where identifier may be omitted.
 */
export type BasicCustomFieldDefinitionInput = BaseCustomFieldProperties & {
  identifier: string;
};

export type SelectCustomFieldDefinitionInput = BasicCustomFieldDefinitionInput &
  SelectFieldProperties;

export type MultiSelectCustomFieldDefinitionInput =
  BasicCustomFieldDefinitionInput & MultiSelectFieldProperties;

export type UploadCustomFieldDefinitionInput = BasicCustomFieldDefinitionInput &
  UploadFieldProperties;

export type SimpleCustomFieldDefinitionInput = BasicCustomFieldDefinitionInput &
  SimpleFieldProperties;

/**
 * Fieldset definition with guaranteed identifier.
 * Recursive - runtime validation enforces MAX_FIELDSET_DEPTH limit.
 */
export type FieldsetCustomFieldDefinitionInput =
  BasicCustomFieldDefinitionInput & {
    children: (
      | SelectCustomFieldDefinitionInput
      | MultiSelectCustomFieldDefinitionInput
      | SimpleCustomFieldDefinitionInput
      | UploadCustomFieldDefinitionInput
      | FieldsetCustomFieldDefinitionInput
    )[];
    type: 'fieldset';
  };

/**
 * Union of all custom field definition types, with guaranteed identifier.
 */
export type CustomFieldDefinitionInput =
  | FieldsetCustomFieldDefinitionInput
  | SimpleCustomFieldDefinitionInput
  | SelectCustomFieldDefinitionInput
  | MultiSelectCustomFieldDefinitionInput
  | UploadCustomFieldDefinitionInput;

import type { DefinedError, ErrorObject } from 'ajv';

// ajv doesn't export any types for errors with `keyword='errorMessage'`
type ErrorMessage = ErrorObject<
  'errorMessage',
  { errors: (DefinedError & { emUsed: boolean })[] }
>;
export type AjvError = ErrorMessage | DefinedError;
